
/**
 * Module dependencies.
 */

import { Button, IconButton, Text, closeIcon16 } from 'slyk-web-libs/design-system';
import { color, media } from 'react-components/styles';
import { ifNotProp, theme } from 'styled-tools';
import { useTranslate } from 'slyk-web-libs/i18n';
import React, { Ref, forwardRef } from 'react';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  goToDashboard: string;
  isVisible: boolean;
  onDismiss: () => void;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<{ isVisible: boolean}>`
  background-color: ${color('info100')};
  min-height: 64px;
  padding: 12px 16px;
  position: sticky;
  top: 0;
  width: 100%;
  z-index: ${theme('zIndex.banner')};

  ${ifNotProp('isVisible', css`
    display: none;
  `)}
`;

/**
 * `ContentWrapper` styled component.
 */

const ContentWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  position: relative;
`;

/**
 * `Content` styled component.
 */

const Content = styled.div`
  align-items: center;
  column-gap: 24px;
  display: flex;
`;

/**
 * `Description` styled component.
 */

const Description = styled(Text).attrs({ size: 'small' })`
  color: ${color('secondary')};
  display: none;

  ${media.min('xs')`
    display: block;
  `}
`;

/**
 * `GoToDashboardButton` styled component.
 */

const GoToDashboardButton = styled(Button)`
  width: max-content;
`;

/**
 * `StyledIconButton` styled component.
 */

const StyledIconButton = styled(IconButton)`
  position: absolute;
  right: 0;
`;

/**
 * `Banner` component.
 */

function Banner(props: Props, forwardedRef?: Ref<HTMLDivElement>): JSX.Element {
  const { goToDashboard, isVisible, onDismiss } = props;
  const { translate } = useTranslate();

  return (
    <Wrapper
      {...forwardedRef ? { ref: forwardedRef } : {}}
      isVisible={isVisible}
    >
      <ContentWrapper>
        <Content>
          <Description>
            {translate('banner.description')}
          </Description>

          <GoToDashboardButton
            href={goToDashboard}
            size={'medium'}
          >
            {translate('common.actions.goToDashboard')}
          </GoToDashboardButton>
        </Content>

        <StyledIconButton
          aria-label={translate('common.actions.close')}
          colorTheme={'transparent'}
          icon={closeIcon16}
          iconSize={'small'}
          onClick={onDismiss}
          size={'16px'}
        />
      </ContentWrapper>
    </Wrapper>
  );
}

/**
 * Wrapped `Banner`.
 */

const WrappedBanner = forwardRef(Banner);

/**
 * Export `Banner` component.
 */

export { WrappedBanner as Banner };
